import {
    createContext,
    ReactNode,
    Ref,
    useCallback,
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { Location, Params, To, useLocation, useNavigate, useParams } from 'react-router-dom';
import { t } from 'utils/getTranslations';
import type { CreateRecordType } from './recordTypes';

/** What an entry point calls to start a creation. */
export type CreateDialogOpener = {
    /**
     * Opens the create dialog over the current page. `false` when `to` is not a create URL, so the caller
     * navigates instead. `true` when it is, also when nothing opens yet: the user may not open it, which is
     * silent, or a creation something was typed in exists, and the user is asked what becomes of it first.
     */
    open: (to: To, state?: unknown) => boolean;
};

const OpenerContext = createContext<CreateDialogOpener | null>(null);

export const CreateDialogOpenerProvider = OpenerContext.Provider;

/** The opener, or `null` where no create dialog exists: the classic design, and anything above the layout. */
export function useCreateDialog(): CreateDialogOpener | null {
    return useContext(OpenerContext);
}

/**
 * Opens a create URL in the create dialog, over the current page, where there is one, and goes to its page
 * anywhere else. The page is told nothing of where it was opened from: a caller whose create page returns
 * there uses `useNavigateWithReturnState`.
 */
export function useOpenCreateUrl(): (url: string) => void {
    const createDialog = useContext(OpenerContext);
    const navigate = useNavigate();

    return useCallback(
        (url: string) => {
            if (!createDialog?.open(url)) {
                navigate(url);
            }
        },
        [createDialog, navigate]
    );
}

const MinimisedContext = createContext(false);

export const CreateDialogMinimisedProvider = MinimisedContext.Provider;

/**
 * Whether a creation is minimised to its tab, which then sits in the window's bottom row, for what keeps clear
 * of that row. `false` where no create dialog exists.
 */
export function useIsCreateDialogMinimised(): boolean {
    return useContext(MinimisedContext);
}

const OpenContext = createContext(false);

export const CreateDialogOpenProvider = OpenContext.Provider;

/** Whether a creation is open over the page, not minimised, for what stays under it. `false` where none exists. */
export function useIsCreateDialogOpen(): boolean {
    return useContext(OpenContext);
}

const SavedListContext = createContext<(() => void) | null>(null);

export const CreateDialogSavedListProvider = SavedListContext.Provider;

/** Shows the Saved for later list, which the create dialog's host holds. `null` where no create dialog exists. */
export function useShowSavedForLater(): (() => void) | null {
    return useContext(SavedListContext);
}

export type CreateDialogFinishOptions = {
    /** Router state for the page behind the dialog, which is re-entered with it by a REPLACE of its own URL. */
    pageState?: unknown;
    /** The creation ends as a draft saved, not as the record created. */
    draft?: boolean;
};

export type CreateDialogForm = UseFormReturn<FieldValues>;

/**
 * What a body that keeps its values in state of its own, with no react-hook-form, hands the dialog in place of a
 * form: what it holds by field, and word of each change.
 */
export type CreateDialogFormAdapter = {
    getValues: () => FieldValues;
    /** Returns the way to stop hearing of changes. */
    subscribe: (onChange: () => void) => () => void;
};

/** One creation in the dialog: what the form inside it reads instead of the router, and its way out. */
export type CreateDialogRequest = {
    record: CreateRecordType;
    /** The create URL that was opened and the router state its opener passed, shaped as the router's own. */
    location: Location;
    /** The create route's params, read off that URL. */
    params: Readonly<Params<string>>;
    /** The record a copy or a duplicate starts from; `null` for a creation from nothing. */
    sourceId: number | null;
    /** Closes the dialog. When something was typed in it, the user says first what becomes of that. */
    close: () => void;
    /**
     * Ends a creation: the dialog closes, or starts the same request again while `createAnother` is on. `target`
     * is where the page form would have gone; nothing navigates there, and the creation's toast offers to.
     */
    finish: (target?: To, options?: CreateDialogFinishOptions) => void;
    /**
     * Whether a create dialog is open as this is called, which a render cannot say: a form that has just
     * finished is still mounted, and goes on to open its follow-up popups, over the page when the dialog closed
     * and above the dialog when it stayed open. A minimised dialog is not open.
     */
    isOpen: () => boolean;
    /** Whether a creation that finishes leaves the dialog open, on the same start, for the next one. */
    createAnother: boolean;
    /** `null` where nothing can keep the dialog open, which then offers no such choice. */
    setCreateAnother: ((on: boolean) => void) | null;
    /** Whether the dialog is a tab on the bottom edge. Its form stays mounted, hidden with the dialog. */
    minimised: boolean;
    /** `null` where nothing can show that tab, and the dialog then offers no way to minimise. */
    minimise: (() => void) | null;
    /**
     * What the user typed, by field: each value of the form that is another than as the user first acted on the
     * dialog. What the form set by itself until then, a context that arrived filled, the values of a copy or of a
     * draft, is not something typed. `null` when nothing was, once the creation has finished, and for a body that
     * handed the dialog neither a form nor an adapter.
     */
    typedValues: () => FieldValues | null;
    /** Whether `typedValues` holds anything. */
    hasTyped: () => boolean;
    /** How many required fields are left to fill, as the Required section last counted; 0 for a body without one. */
    toFill: () => number;
    /**
     * The form's react-hook-form methods, once it has called `useCreateDialogForm`. After a creation that left
     * the dialog open they are the finished form's until the one mounted in its place has registered.
     */
    form: CreateDialogForm | null;
    /**
     * What stands in for `form` where the body has no react-hook-form, once it has called `useCreateDialogValues`.
     * What was typed goes by it before `form`.
     */
    adapter: CreateDialogFormAdapter | null;
    /**
     * Saves the creation as a draft and ends it, as the form's Save as draft does: `false`, with the form showing
     * why, when a rule or the server refused it. `null` where the form offers no draft.
     */
    saveDraft: (() => Promise<boolean>) | null;
    /** Where the creation started: "For {name}", "Copy of {number}". */
    chip: string | null;
    /** The same where the title already says a creation is meant: the name alone, "Copy of {number}". */
    chipName: string | null;
    /** The chip's parts, for a sentence that names where the creation started in words of its own. */
    origin: CreateDialogOrigin | null;
    /** The header and footer elements the form's chrome portals into; `errorSlot` is before `footerSlot`. */
    headerSlot: HTMLElement | null;
    footerSlot: HTMLElement | null;
    errorSlot: HTMLElement | null;
    /** Called by the dialog as the user acts on it, before the act changes anything: see `hasTyped`. */
    noteUserAction: () => void;
    registerForm: (form: CreateDialogForm) => void;
    registerAdapter: (adapter: CreateDialogFormAdapter) => void;
    registerDraftSave: (save: (() => Promise<boolean>) | null) => void;
    setToFill: (count: number) => void;
    /** `name` is `chip` where it is left out. */
    setChip: (chip: string | null, name?: string | null, origin?: CreateDialogOrigin | null) => void;
    setHeaderSlot: (element: HTMLElement | null) => void;
    setFooterSlot: (element: HTMLElement | null) => void;
    setErrorSlot: (element: HTMLElement | null) => void;
};

/** Where a creation started: `for` a parent, by its name, or as a `copy` of a record, by its number. */
export type CreateDialogOrigin = { origin: 'for' | 'copy'; value: string };

const RequestContext = createContext<CreateDialogRequest | null>(null);

type ProviderProps = Pick<CreateDialogRequest, 'record' | 'location' | 'params'> & {
    sourceId?: number | null;
    onClose: CreateDialogRequest['close'];
    onFinish: CreateDialogRequest['finish'];
    /** The host's answer. Without a host the request is all there is, and it counts as open. */
    isOpen?: CreateDialogRequest['isOpen'];
    createAnother?: boolean;
    /** Given by a host that can start the request again. */
    onCreateAnotherChange?: (on: boolean) => void;
    minimised?: boolean;
    /** Given by a host that can show the dialog as a tab. */
    onMinimise?: () => void;
    /** Receives the request as it stands, for a host that has to read it from outside the dialog. */
    ref?: Ref<CreateDialogRequest>;
    children: ReactNode;
};

const alwaysOpen = () => true;

/**
 * A value as text, which is how two moments of it are compared. `null` for one that cannot be written down,
 * which then counts as changed: asking about nothing costs less than dropping something.
 *
 * ponytail: JSON, so a file swapped for another file reads as no change. Compare the values themselves if a
 * form's only edit can be that.
 */
function textOf(value: unknown): string | null | undefined {
    try {
        return JSON.stringify(value);
    } catch {
        return null;
    }
}

type Snapshot = Record<string, string | null | undefined>;

/** What a creation holds, field by field, as text. */
function snapshotOf(source: CreateDialogFormAdapter): Snapshot {
    return Object.fromEntries(Object.entries(source.getValues()).map(([name, value]) => [name, textOf(value)]));
}

/**
 * Holds one request's state. Mounted afresh for each request, so nothing carries over from the last one. A
 * request that is started again after a creation keeps it: the form mounted in its place registers over the
 * finished one and sets the chip again.
 */
export function CreateDialogRequestProvider({
    record,
    location,
    params,
    sourceId = null,
    onClose,
    onFinish,
    isOpen = alwaysOpen,
    createAnother = false,
    onCreateAnotherChange,
    minimised = false,
    onMinimise,
    ref,
    children,
}: ProviderProps) {
    const [form, setForm] = useState<CreateDialogForm | null>(null);
    const [adapter, registerAdapter] = useState<CreateDialogFormAdapter | null>(null);
    const [draftSave, setDraftSave] = useState<{ save: () => Promise<boolean> } | null>(null);
    const registerDraftSave = useCallback(
        (save: (() => Promise<boolean>) | null) => setDraftSave(save && { save }),
        []
    );
    const [chip, setChipWords] = useState<string | null>(null);
    const [chipName, setChipName] = useState<string | null>(null);
    const [origin, setOrigin] = useState<CreateDialogOrigin | null>(null);
    const setChip = useCallback<CreateDialogRequest['setChip']>((words, name = words, from = null) => {
        setChipWords(words);
        setChipName(name);
        setOrigin(from);
    }, []);
    const [headerSlot, setHeaderSlot] = useState<HTMLElement | null>(null);
    const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
    const [errorSlot, setErrorSlot] = useState<HTMLElement | null>(null);
    // What the creation held as the user first acted on the dialog, with what it was read from: a request that
    // is started again after a creation gets another form, whose first action is another.
    const baselineRef = useRef<{ source: unknown; values: Snapshot } | null>(null);
    // A ref rather than state: the count changes as the user types, and nothing in the dialog draws by it.
    const toFillRef = useRef(0);
    const toFill = useCallback(() => toFillRef.current, []);
    const setToFill = useCallback((count: number) => {
        toFillRef.current = count;
    }, []);

    // Compared by `control`: a form hook that hands back a new object around the same form must not
    // re-register on every render.
    const registerForm = useCallback(
        (next: CreateDialogForm) => setForm((current) => (current?.control === next.control ? current : next)),
        []
    );

    // Where the creation's values are read: the body's adapter where it handed one over, else its form, read as
    // one. One object per form or adapter, which is how the baseline tells two of them apart.
    const source = useMemo<CreateDialogFormAdapter | null>(
        () =>
            adapter ??
            (form && {
                getValues: form.getValues,
                subscribe: (onChange) => form.subscribe({ formState: { values: true }, callback: onChange }),
            }),
        [adapter, form]
    );

    const noteUserAction = useCallback(() => {
        if (source && baselineRef.current?.source !== source) {
            baselineRef.current = { source, values: snapshotOf(source) };
        }
    }, [source]);

    const typedValues = useCallback((): FieldValues | null => {
        const baseline = baselineRef.current;
        if (!source || !baseline || baseline.source !== source) {
            return null;
        }
        const values = source.getValues();
        const typed: FieldValues = {};
        for (const name of new Set([...Object.keys(baseline.values), ...Object.keys(values)])) {
            const now = textOf(values[name]);
            if (now === null || now !== baseline.values[name]) {
                typed[name] = values[name];
            }
        }
        return Object.keys(typed).length > 0 ? typed : null;
    }, [source]);

    const hasTyped = useCallback(() => typedValues() !== null, [typedValues]);

    // Once the creation has finished nothing in it counts as typed, though its form stays registered until the
    // dialog closes or the next form takes its place.
    const finish = useCallback<CreateDialogRequest['finish']>(
        (...created) => {
            baselineRef.current = null;
            onFinish(...created);
        },
        [onFinish]
    );

    const request = useMemo<CreateDialogRequest>(
        () => ({
            record,
            location,
            params,
            sourceId,
            close: onClose,
            finish,
            isOpen,
            createAnother,
            setCreateAnother: onCreateAnotherChange ?? null,
            minimised,
            minimise: onMinimise ?? null,
            typedValues,
            hasTyped,
            toFill,
            form,
            adapter,
            saveDraft: draftSave?.save ?? null,
            chip,
            chipName,
            origin,
            headerSlot,
            footerSlot,
            errorSlot,
            noteUserAction,
            registerForm,
            registerAdapter,
            registerDraftSave,
            setToFill,
            setChip,
            setHeaderSlot,
            setFooterSlot,
            setErrorSlot,
        }),
        [
            record,
            location,
            params,
            sourceId,
            onClose,
            finish,
            isOpen,
            createAnother,
            onCreateAnotherChange,
            minimised,
            onMinimise,
            typedValues,
            hasTyped,
            toFill,
            form,
            adapter,
            draftSave,
            chip,
            chipName,
            origin,
            headerSlot,
            footerSlot,
            errorSlot,
            noteUserAction,
            registerForm,
            registerDraftSave,
            setToFill,
            setChip,
        ]
    );

    useImperativeHandle(ref, () => request, [request]);

    return <RequestContext.Provider value={request}>{children}</RequestContext.Provider>;
}

/** The creation this component is part of, or `null` outside the create dialog. */
export function useCreateDialogRequest(): CreateDialogRequest | null {
    return useContext(RequestContext);
}

/** True in the create dialog's own body. A popup opened from it is not the dialog: see `useOpenedFromCreateDialog`. */
export function useInCreateDialog(): boolean {
    return useContext(RequestContext) !== null;
}

/**
 * The route a create form reads: the dialog's request inside the create dialog, the router's outside it. In the
 * dialog the router still describes the page behind, so a form reads its params, its location state and its
 * query from here.
 */
export function useCreateRoute() {
    const request = useContext(RequestContext);
    const routerParams = useParams();
    const routerLocation = useLocation();
    const location = request?.location ?? routerLocation;

    return {
        params: request?.params ?? routerParams,
        location,
        inDialog: request !== null,
    };
}

/** Hands the form to the dialog it renders in, which reads its state and sets its values. Nothing on a page. */
export function useCreateDialogForm<T extends FieldValues>(methods: UseFormReturn<T>): void {
    const registerForm = useContext(RequestContext)?.registerForm;

    useEffect(() => {
        registerForm?.(methods as unknown as CreateDialogForm);
    }, [registerForm, methods]);
}

/**
 * Hands the dialog the values of a body that keeps them in state of its own, in place of a form: `values` by
 * field, plain data and the same object until one of them changes. Nothing on a page.
 */
export function useCreateDialogValues(values: FieldValues): void {
    const registerAdapter = useContext(RequestContext)?.registerAdapter;
    const latest = useRef(values);
    const listeners = useRef(new Set<() => void>());

    useEffect(() => {
        latest.current = values;
        listeners.current.forEach((onChange) => onChange());
    }, [values]);

    useEffect(() => {
        // One set for the life of the body: a listener leaves the set it joined.
        const heard = listeners.current;
        registerAdapter?.({
            getValues: () => latest.current,
            subscribe: (onChange) => {
                heard.add(onChange);
                return () => {
                    heard.delete(onChange);
                };
            },
        });
    }, [registerAdapter]);
}

/** Says in the dialog's header where the creation started: a copy's source wins over a parent. Nothing on a page. */
export function useCreateDialogChip({ parent, copyOf }: { parent?: string | null; copyOf?: string | null }): void {
    const setChip = useContext(RequestContext)?.setChip;
    let chip: string | null = null;
    let name: string | null = null;
    if (setChip && copyOf) {
        chip = t('create-dialog.context.copy', { number: copyOf });
        name = chip;
    } else if (setChip && parent) {
        chip = t('create-dialog.context.for', { name: parent });
        name = parent;
    }

    useEffect(() => {
        const value = copyOf || parent;
        setChip?.(chip, name, chip && value ? { origin: copyOf ? 'copy' : 'for', value } : null);
    }, [setChip, chip, name, copyOf, parent]);
}

const OpenedFromContext = createContext(false);

export const OpenedFromCreateDialogProvider = OpenedFromContext.Provider;

/**
 * True in a popup that was opened from the create dialog while it was open, or from a popup that was. The
 * dialog's own context does not reach a popup, which renders above the layout, so the popup stack carries this
 * instead.
 */
export function useOpenedFromCreateDialog(): boolean {
    return useContext(OpenedFromContext);
}
